import { Field, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import GraphQLJSON from 'graphql-type-json'
import type { MetadataType } from '../../base/Metadata'
import { Metadata } from '../../base/Metadata'
import { BaseEntity } from '../../base/BaseEntity'
import { generateEntityId } from '../../generateEntityId'

/**
 * Raw snapshot of a PayMongo checkout session / payment, keyed by the gateway's own
 * checkout session id. Mirrors `StripePaymentIntent` — a gateway-internal raw snapshot lives
 * in the contract layer so it can be typed by any consumer (the gateway itself, integration
 * tests), even though it is never returned through GraphQL.
 */
@ObjectType()
@InputType('PayMongoPaymentInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'payMongoPayments' } })
export class PayMongoPayment extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('pmp') })
  declare _id: string

  /** PayMongo checkout session id (`cs_...`) — what we look this row up by after redirect/webhook. */
  @Field(() => String)
  @Prop({ type: String, required: true })
  checkoutSessionId: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  checkoutStatus?: string

  /**
   * The Checkout Sessions API has no `metadata` field — `reference_number` is the only
   * merchant-defined pass-through slot, so it carries our `CreditPurchase._id`.
   */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  referenceNumber?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  paymentIntentId?: string

  @Field(() => Boolean, { nullable: true })
  @Prop({ type: Boolean })
  livemode?: boolean

  /** Full raw checkout-session/payment payload PayMongo returned, kept for audit/debugging. */
  @Field(() => GraphQLJSON, { nullable: true })
  @Prop({ type: Metadata })
  raw?: MetadataType
}
